// src/components/applications/ApplicationSelection.js
import React, { memo } from "react";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";

import { formattedDeadlineDate } from "../utils/dateHelpers";

function ApplicationSelection({
  agreementLocal,
  selectedApplications,
  setSelectedApplications,
  generalClose,
  dasClose,
  handleSelectApplicationSubmit,
  handleAgreeChange,
}) {
  const genSelected = selectedApplications.includes("general");
  const dasSelected = selectedApplications.includes("das");

  const handleGenChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedApplications((prev) => [...prev, "general"]);
    } else {
      setSelectedApplications((prev) =>
        prev.filter((app) => app !== "general"),
      );
    }
  };

  const handleDasChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedApplications((prev) => [...prev, "das"]);
    } else {
      setSelectedApplications((prev) => prev.filter((app) => app !== "das"));
    }
  };

  const handleRadioChange = (e) => {
    const value = e.target.value;
    handleAgreeChange(value);
  };

  return (
    <Box
      className="home-page"
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 2, sm: 2 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Welcome to NLSF Awards Applications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please review the acknowledgement below and choose the
              application(s) you would like to complete.
            </Typography>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2.25}>
            {/* Collection / Agreement */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 2,
              }}
            >
              <Stack spacing={1.25}>
                <Typography sx={{ fontWeight: 900 }}>
                  Collection of Information Acknowledgement
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  For the purpose of processing Nanaimo-Ladysmith Schools
                  Foundations Scholarship/Award/Bursary applications, students’
                  personal information will be collected under the authority of
                  the Freedom of Information and Protection of Privacy Act,
                  Section 26(c). Students’ personal information is used for the
                  purpose of verifying eligibility, selecting successful
                  candidates, and processing the scholarship payment.
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  The names of the scholarship/bursary winners, the school
                  attended and the amount of the award may be made public
                  through the media and the Internet. The students name and/or
                  photo and contact information may also be shared with the
                  contributor of the award.
                </Typography>

                <Divider />

                <Typography sx={{ fontWeight: 900 }}>Do you agree?</Typography>

                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-label="agreement"
                    name="agreement"
                    value={agreementLocal || ""}
                    onChange={handleRadioChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="YES"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="NO"
                    />
                  </RadioGroup>
                </FormControl>

                {agreementLocal !== "yes" && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    You must select <strong>YES</strong> to continue.
                  </Alert>
                )}
              </Stack>
            </Paper>

            {/* Selection header */}
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Select Application(s) You Would Like to Complete
            </Typography>

            {/* Two cards layout */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              {/* General */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 2,
                  height: "100%",
                }}
              >
                <Stack spacing={1.25}>
                  <Typography sx={{ fontWeight: 900 }}>
                    General Applications
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    The Nanaimo-Ladysmith Schools Foundation offers a variety of
                    awards, scholarships and bursaries to graduating students
                    from the Nanaimo-Ladysmith Public Schools (District 68).
                    These awards are graciously funded by local businesses,
                    organizations, and individuals who are committed to
                    supporting students in their pursuit of post-secondary
                    education.
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    These awards are available to graduates of Nanaimo-Ladysmith
                    Public Schools (District 68) who are Canadian Citizens or
                    hold permanent residency status in Canada.{" "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 900, color: "error.main" }}
                    >
                      International students, including those on study permits
                      or visas, are not eligible to apply.
                    </Box>
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    This wizard will help you to complete your scholarship and
                    bursary application.
                  </Typography>

                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Please fill out all required fields to the best of your
                    ability and click <strong>“Save and Continue”</strong> to
                    proceed to the next part. You can also save your progress
                    and exit by clicking <strong>“Save and Exit”</strong>.
                    <br />
                    You can return any time to complete the process{" "}
                    <strong>
                      until the closing deadline of{" "}
                      {formattedDeadlineDate(generalClose)} @ Noon.
                    </strong>
                  </Alert>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={genSelected}
                        onChange={handleGenChange}
                        disabled={agreementLocal !== "yes"}
                      />
                    }
                    label="I would like to apply for General Application Awards"
                  />
                </Stack>
              </Paper>

              {/* District/Authority */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 2,
                  height: "100%",
                }}
              >
                <Stack spacing={1.25}>
                  <Typography sx={{ fontWeight: 900 }}>
                    Ministry of Education: District/Authority Scholarship
                    Guidelines
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    The District/Authority Scholarships recognize graduating BC
                    students for excellence in their chosen areas of interest or
                    strength. These scholarships are intended to help students
                    pursue post-secondary education.
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    The Ministry will allocate 5500 awards pro-rated to school
                    districts and independent school authorities based on their
                    September 30th Grade 12 enrolments. The award is a $1250
                    voucher that can be used toward post-secondary education
                    tuition.
                  </Typography>

                  <Typography variant="body2">
                    <Link
                      href="https://www2.gov.bc.ca/gov/content/education-training/k-12/support/scholarships/provincial-scholarships/district-authority-scholarships"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontWeight: 800 }}
                    >
                      Click here to check eligibility
                    </Link>
                  </Typography>

                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    This year’s application deadline is{" "}
                    <strong>{formattedDeadlineDate(dasClose)}</strong>.
                  </Alert>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dasSelected}
                        onChange={handleDasChange}
                        disabled={agreementLocal !== "yes"}
                      />
                    }
                    label="I would like to apply for a District Authority Scholarship"
                  />
                </Stack>
              </Paper>

              {/* Additional Applications */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 2,
                  height: "100%",
                }}
              >
                <Stack spacing={1.25}>
                  <Typography sx={{ fontWeight: 900 }}>
                    Additional Applications
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Additional Applications In addition to the awards offered
                    through the General Application, a number of awards require
                    additional applications or supplementary information. To
                    apply for these awards, you must first complete the{" "}
                    <strong>General Application</strong>.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Once your <strong>General Application</strong> is submitted,
                    you may apply for any additional awards you are eligible for
                    by completing the required supplementary
                    essays/application(s).
                  </Typography>

                  <Typography variant="body2">
                    View the list of available awards requiring additional
                    applications here:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Link
                      href="/awards-page"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontWeight: 800 }}
                    >
                      Click here to check eligibility
                    </Link>
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            {/* Footer action */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
              <Button
                variant="contained"
                onClick={handleSelectApplicationSubmit}
                disabled={
                  agreementLocal !== "yes" || selectedApplications.length === 0
                }
                sx={{ px: 3, py: 1.2, borderRadius: 2 }}
              >
                Proceed to Application(s)
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default memo(ApplicationSelection);
